import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { DashboardFinances } from '@worki/shared';
import { currentLocale } from '../i18n.js';
import { money } from '../lib/format.js';

/** Approval-month quote value, separate from detected invoice income. */
export function DashboardRevenue({ finances, onMonthChange }: { finances: DashboardFinances; onMonthChange?: (delta: number) => void }) {
  const { t } = useTranslation();
  const locale = currentLocale();
  const current = finances.months.at(-1);
  const maximum = Math.max(1, ...finances.months.map((month) => month.approvedTotal));
  const monthLabel = (month: string) => new Intl.DateTimeFormat(locale === 'he' ? 'he-IL' : 'en-GB', {
    month: 'short', year: '2-digit', timeZone: 'UTC',
  }).format(new Date(`${month}-15T12:00:00Z`));
  const amount = (value: number) => money(value, finances.currency, locale);

  return (
    <section className="rounded-2xl border border-border bg-card p-4 space-y-4 animate-fade-up" aria-labelledby="dashboard-revenue-title">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 id="dashboard-revenue-title" className="text-base font-bold">{t('dashboard.finances.expectedRevenue')}</h2>
          <p className="text-xs text-muted-foreground">{t('dashboard.finances.sixMonths')} · {finances.currency}</p>
        </div>
        {current && <div className="text-end shrink-0">
          <p className="text-xs text-muted-foreground">{monthLabel(current.month)}</p>
          <p className="text-xl font-bold ltr-nums">{amount(current.approvedTotal)}</p>
          <Link to="/quotes?tab=approved" className="min-h-11 inline-flex items-center text-sm text-primary hover:underline">{t('dashboard.finances.approvedCount', { count: current.approvedCount })}</Link>
        </div>}
      </div>
      {onMonthChange && <div className="flex items-center justify-between">
        <button type="button" className="min-h-11 min-w-11 grid place-items-center rounded-lg hover:bg-muted" aria-label={t('completion.previous')} onClick={() => onMonthChange(-1)}><ChevronLeft className="h-5 w-5 rtl:rotate-180" /></button>
        <span className="text-sm">{current && monthLabel(current.month)}</span>
        <button type="button" className="min-h-11 min-w-11 grid place-items-center rounded-lg hover:bg-muted" aria-label={t('completion.next')} onClick={() => onMonthChange(1)}><ChevronRight className="h-5 w-5 rtl:rotate-180" /></button>
      </div>}
      <div role="img" aria-label={`${t('dashboard.finances.expectedRevenue')} · ${t('dashboard.finances.sixMonths')} · ${finances.currency}`}>
        <div className="grid gap-3 items-end h-40 border-b border-border" style={{ gridTemplateColumns: `repeat(${Math.max(1, finances.months.length)}, minmax(0, 1fr))` }}>
          {finances.months.map((month) => (
            <div key={month.month} className="h-full flex items-end justify-center px-1" title={`${monthLabel(month.month)}: ${amount(month.approvedTotal)}`}>
              <div className="w-full max-w-14 rounded-t-md bg-primary/80" style={{ height: `${Math.max(0, month.approvedTotal) / maximum * 100}%` }} />
            </div>
          ))}
        </div>
        <div className="grid gap-3 pt-2 text-center text-[11px] text-muted-foreground" style={{ gridTemplateColumns: `repeat(${Math.max(1, finances.months.length)}, minmax(0, 1fr))` }}>
          {finances.months.map((month) => <span key={month.month}>{monthLabel(month.month)}</span>)}
        </div>
      </div>
      <dl className="sr-only">
        {finances.months.map((month) => <div key={month.month}>
          <dt>{month.month}</dt>
          <dd>{amount(month.approvedTotal)} · {t('dashboard.finances.approvedCount', { count: month.approvedCount })}</dd>
        </div>)}
      </dl>
    </section>
  );
}
